import React from "react";
import { ButtonProps, Divider, Flex, FlexProps, PopoverContentProps, Text } from "@chakra-ui/react";
import { colours } from "@/utils/themeUtils";

/** Same surface as the applied Focus pill (`colours.blue[600]`). */
export const floatingToolbarBg = colours.blue[600];

export const floatingToolbarGhostButtonProps: ButtonProps = {
	size: "sm",
	variant: "ghost",
	color: "white",
	_hover: { bg: "whiteAlpha.200" },
	_active: { bg: "whiteAlpha.300" },
};

/** Dark popup panel that matches the floating toolbar (Focus / Pause / Screenshare menus). */
export const floatingToolbarPopoverContentProps: PopoverContentProps = {
	bg: floatingToolbarBg,
	color: "white",
	border: "1px solid",
	borderColor: "whiteAlpha.200",
	borderRadius: "12px",
	boxShadow: "lg",
	width: "auto",
	minW: "200px",
	_focus: { outline: "none", boxShadow: "lg" },
};

/** Action rows inside the toolbar popup (replaces light MenuItems). */
export const floatingToolbarMenuItemProps: ButtonProps = {
	size: "sm",
	variant: "ghost",
	color: "white",
	justifyContent: "flex-start",
	fontWeight: 500,
	width: "100%",
	borderRadius: "8px",
	px: 3,
	py: 2,
	height: "auto",
	whiteSpace: "nowrap",
	_hover: { bg: "whiteAlpha.200" },
	_active: { bg: "whiteAlpha.300" },
	_disabled: { opacity: 0.4, cursor: "not-allowed", _hover: { bg: "transparent" } },
};

/** Shared styles for dark toolbar menus (same dark surface as the toolbar). */
export const floatingToolbarMenuListProps = {
	bg: floatingToolbarBg,
	color: "white",
	border: "1px solid",
	borderColor: "whiteAlpha.200",
	borderRadius: "12px",
	boxShadow: "lg",
	py: 2,
	overflow: "visible" as const,
};

interface FloatingToolbarProps extends FlexProps {
	bottom?: string;
}

export function FloatingToolbar({ bottom = "24px", children, zIndex = 999, ...rest }: FloatingToolbarProps): JSX.Element {
	return (
		<Flex
			position="fixed"
			bottom={bottom}
			left="50%"
			transform="translateX(-50%)"
			bg={floatingToolbarBg}
			color="white"
			borderRadius="full"
			boxShadow="lg"
			px="20px"
			py="10px"
			gap="12px"
			align="center"
			flexWrap="nowrap"
			whiteSpace="nowrap"
			zIndex={zIndex}
			{...rest}
		>
			{children}
		</Flex>
	);
}

export function FloatingToolbarTitle({ children }: { children: React.ReactNode }): JSX.Element {
	return (
		<Text fontWeight="600" color="white" flexShrink={0} whiteSpace="nowrap">
			{children}
		</Text>
	);
}

export function FloatingToolbarDivider(): JSX.Element {
	return <Divider orientation="vertical" height="24px" borderColor="whiteAlpha.300" />;
}
